import type { TaxonomyClassFields } from "@sitescene/types";

import { useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { Input } from "@/components/ui/input/input";
import { InputNumber } from "@/components/ui/input/input-number";
import { Label } from "@/components/ui/input/label";
import { NativeSelect } from "@/components/ui/select/select-native";
import { TextArea } from "@/components/ui/textarea/textarea";

import { ListInput } from "./ListInput";
import { CLASS_ID_PATTERN } from "./taxonomy-view";

interface ClassEditorProps {
	busy: boolean;
	/** Absent when creating: the id is typed in the form. */
	classId?: string;
	classOptions: { label: string; value: string }[];
	error: Error | null;
	/**
	 * What the form starts from when that differs from `value`, the baseline it
	 * is compared against — a suggestion to review, say. Defaults to `value`.
	 */
	initial?: { classId?: string; fields: TaxonomyClassFields };
	/** Fires as Alertable is toggled, before the class is saved. */
	onAlertableChange?: (alertable: boolean) => void;
	onRevert?: () => void;
	onSave: (classId: string, fields: TaxonomyClassFields) => void;
	staged: boolean;
	value: TaxonomyClassFields;
}

const probability = (logOdds: number) => 1 / (1 + Math.exp(-logOdds));

/** NumberField passes NaN when the field is cleared. */
const numberOrZero = (n: number) => (Number.isNaN(n) ? 0 : n);

/**
 * Every field of one class. Edits stay local until saved to the draft; the
 * parent remounts it (by `key`) to start over from another value.
 */
export const ClassEditor = ({
	busy,
	classId,
	classOptions,
	error,
	initial,
	onAlertableChange,
	onRevert,
	onSave,
	staged,
	value,
}: ClassEditorProps) => {
	const creating = classId === undefined;
	const [id, setId] = useState(classId ?? initial?.classId ?? "");
	const [fields, setFields] = useState(initial?.fields ?? value);
	const patch = (changes: Partial<TaxonomyClassFields>) =>
		setFields((current) => ({ ...current, ...changes }));

	const others = classOptions.filter((option) => option.value !== id);
	const idValid = !creating || CLASS_ID_PATTERN.test(id);
	const { falseNegative, falsePositive } = fields.costs;
	const costsValid = falseNegative > 0 && falsePositive > 0;
	// An active class with no description can never be chosen well.
	const described = !fields.active || Boolean(fields.description?.trim());
	const valid =
		idValid && fields.displayName.trim() !== "" && costsValid && described;
	const dirty = creating || JSON.stringify(fields) !== JSON.stringify(value);
	const threshold = costsValid
		? falsePositive / (falsePositive + falseNegative)
		: undefined;
	const prefix = `class-${classId ?? "new"}`;

	return (
		<form
			className="flex flex-col gap-3"
			onSubmit={(event) => {
				event.preventDefault();
				if (valid) onSave(id.trim(), fields);
			}}
		>
			<div className="flex flex-wrap items-end gap-3">
				<Input
					isDisabled={!creating}
					label="Class id"
					onChange={(next) => setId(next.trim())}
					placeholder="weapon.knife"
					value={id}
				/>
				<Input
					label="Display name"
					onChange={(displayName) => patch({ displayName })}
					value={fields.displayName}
				/>
				<Checkbox
					className="pb-1"
					isSelected={fields.alertable}
					label="Alertable"
					onChange={(alertable) => {
						patch({ alertable });
						onAlertableChange?.(alertable);
					}}
				/>
				<Checkbox
					className="pb-1"
					isSelected={fields.active}
					label="Active"
					onChange={(active) => patch({ active })}
				/>
			</div>
			{creating && id && !idValid && (
				<p className="text-sm text-error-primary" role="alert">
					Class ids are family.member in lowercase kebab-case, and can never
					change once published.
				</p>
			)}

			<TextArea
				label="Description (the classifier routes alerts by it and the blind pass names classes by it)"
				onChange={(description) => patch({ description })}
				rows={2}
				value={fields.description ?? ""}
			/>

			{!described && (
				<p className="text-sm text-error-primary" role="alert">
					An active class needs a description: the classifier picks an alert's
					class from it.
				</p>
			)}

			<div className="flex flex-col gap-1.5">
				<Label htmlFor={`${prefix}-confusers`}>Confusers</Label>
				<ListInput
					id={`${prefix}-confusers`}
					onChange={(confusers) => patch({ confusers })}
					options={others}
					values={fields.confusers}
				/>
			</div>

			<div className="flex flex-wrap items-end gap-3">
				<InputNumber
					label="Cost of a miss"
					minValue={0}
					onChange={(next) =>
						setFields((current) => ({
							...current,
							costs: {
								...current.costs,
								falseNegative: numberOrZero(next),
							},
						}))
					}
					value={falseNegative}
				/>
				<InputNumber
					label="Cost of a false alarm"
					minValue={0}
					onChange={(next) =>
						setFields((current) => ({
							...current,
							costs: {
								...current.costs,
								falsePositive: numberOrZero(next),
							},
						}))
					}
					value={falsePositive}
				/>
				<InputNumber
					label="Default log prior odds"
					onChange={(next) =>
						patch({ defaultLogPriorOdds: numberOrZero(next) })
					}
					value={fields.defaultLogPriorOdds}
				/>
				<NativeSelect
					label="Parent"
					onChange={(event) =>
						patch({ parent: event.target.value || undefined })
					}
					options={[{ label: "none", value: "" }, ...others]}
					value={fields.parent ?? ""}
				/>
			</div>
			<p className="text-xs text-tertiary">
				Prior ≈ {(probability(fields.defaultLogPriorOdds) * 100).toPrecision(2)}
				%
				{threshold !== undefined &&
					` · alerts at ≥ ${(threshold * 100).toPrecision(2)}% belief`}
			</p>

			<div className="flex flex-wrap items-center gap-2">
				<Button isDisabled={busy || !valid || !dirty} size="sm" type="submit">
					{creating ? "Add class to draft" : "Save class to draft"}
				</Button>
				{staged && onRevert && (
					<Button
						color="primary-destructive"
						isDisabled={busy}
						onClick={onRevert}
						size="sm"
						type="button"
					>
						Remove from draft
					</Button>
				)}
				{error && (
					<span className="text-sm text-error-primary" role="alert">
						{error.message}
					</span>
				)}
			</div>
		</form>
	);
};
